/* === AgRating — Anagogia Design System === */

import { useState } from 'react';

import { AG_LABELS } from '../utils/agLabels';

import styles from './AgRating.module.css';

interface AgRatingProps {
  /** Current value (0 to max) */
  value: number | null;
  /** Called when value changes */
  onChange?: (event: React.SyntheticEvent, newValue: number | null) => void;
  /** Maximum number of stars */
  max?: number;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Name attribute for the rating (passed to onChange event target) */
  name?: string;
  /** Whether the rating is read-only */
  readOnly?: boolean;
  /** Whether the rating is disabled */
  disabled?: boolean;
  /**
   * Cible tactile de 44 px par étoile (`--ag-touch-target`) : obligatoire dès
   * que la notation est touchée au doigt (shell mobile). La taille du glyphe
   * reste celle de `size`.
   */
  touchTarget?: boolean;
  /**
   * Libellé de chaque palier (clé : 1 à `max`). Un palier légendé porte son
   * libellé en infobulle (« 5 — … ») et dans son nom accessible. Un libellé
   * vide est ignoré ; sans légende, rien ne change.
   */
  labels?: Partial<Record<number, string>>;
  /**
   * Affiche sous les étoiles le libellé du palier courant, quand il existe.
   * Pour le tactile, où il n'y a pas de survol.
   */
  showValueLabel?: boolean;
  /** Additional class name */
  className?: string;
}

/**
 * Notation par étoiles.
 * @category Formulaires
 */
function AgRating({
  value,
  onChange,
  max = 5,
  size = 'md',
  name,
  readOnly = false,
  disabled = false,
  touchTarget = false,
  labels,
  showValueLabel = false,
  className,
}: AgRatingProps) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const sizeClass = size === 'sm' ? styles.sizeSm : size === 'lg' ? styles.sizeLg : styles.sizeMd;

  const labelOf = (palier: number | null): string | undefined => {
    if (palier === null) return undefined;
    const label = labels?.[palier]?.trim();
    return label || undefined;
  };

  const handleClick = (starValue: number, event: React.MouseEvent) => {
    if (readOnly || disabled || !onChange) return;
    // If clicking the same value, clear it
    const newValue = starValue === value ? null : starValue;
    // Create a synthetic-like event with name on target
    const syntheticEvent = Object.create(event, {
      target: { value: { name, value: String(newValue) } },
    });
    onChange(syntheticEvent, newValue);
  };

  const stars = [];
  for (let i = 1; i <= max; i++) {
    const displayValue = hoverValue ?? value ?? 0;
    const isFilled = i <= displayValue;
    const isHovered = hoverValue !== null && i <= hoverValue;

    const legende = labelOf(i);

    stars.push(
      <button
        key={i}
        type="button"
        role="radio"
        aria-checked={i === value}
        className={[
          styles.star,
          isFilled ? styles.starFilled : '',
          isHovered ? styles.starHover : '',
        ].filter(Boolean).join(' ')}
        onClick={(e) => handleClick(i, e)}
        onMouseEnter={() => !readOnly && !disabled && setHoverValue(i)}
        onMouseLeave={() => setHoverValue(null)}
        disabled={disabled}
        aria-label={`${i} étoile${i > 1 ? 's' : ''} sur ${max}${legende ? `, ${legende}` : ''}`}
        title={legende ? `${i} — ${legende}` : undefined}
        tabIndex={readOnly ? -1 : 0}
      >
        ★
      </button>
    );
  }

  const groupe = (
    <div
      className={[styles.rating, sizeClass, touchTarget ? styles.touchTarget : '', className].filter(Boolean).join(' ')}
      role="radiogroup"
      aria-label={name || AG_LABELS.importance}
    >
      {stars}
    </div>
  );

  if (!showValueLabel) return groupe;

  // L'enveloppe est TOUJOURS rendue avec `showValueLabel` : si elle n'existait
  // qu'avec un libellé, son apparition remonterait les étoiles et ferait perdre
  // le focus (clavier, TalkBack) sur l'étoile qu'on vient d'activer.
  const valueLabel = labelOf(value);
  return (
    <div className={styles.withValueLabel}>
      {groupe}
      {valueLabel && (
        <span className={`text-caption ${styles.valueLabel}`} data-rating-value-label>
          {valueLabel}
        </span>
      )}
    </div>
  );
}

export { AgRating };
export type { AgRatingProps };
export default AgRating;
